import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Default to browser language or English
  const getInitialLanguage = () => {
    const saved = localStorage.getItem('phonestream_language');
    if (saved) return saved;
    
    const browserLang = navigator.language || navigator.userLanguage;
    return browserLang.startsWith('es') ? 'es' : 'en';
  };

  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    localStorage.setItem('phonestream_language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  const translations = {
    en: {
      // Navigation
      home: 'Home',
      dashboard: 'Dashboard',
      store: 'Store',
      play: 'Play',
      marketplace: 'Marketplace',
      admin: 'Admin',
      termsAndConditions: 'Terms & Conditions',
      privacyPolicy: 'Privacy Policy',
      masterServiceAgreement: 'Master Service Agreement',
      wallet: 'Wallet',
      notConnected: 'Not connected',
      callbackDomain: 'Callback Domain',
      gameLaunchUrl: 'Game Launch URL',
      configured: 'Configured',
      missingPlayUrl: 'Missing - set REACT_APP_PLAY_URL or paste below',
      lobbyGameUrl: 'Lobby / game URL',
      playUrlPlaceholder: 'https://example-game-lobby-url',
      openGame: 'Open Game',
      iframeBlockedHint: 'Some providers block iframe embedding. If the frame below is blank, use Open Game.',
      enterLobbyUrlHint: 'Enter a lobby URL above to start testing the game UI flow.',
      playSubtitle: 'Launch your casino and game lobby from here. Seamless wallet callbacks are already integrated on your backend.',

      // Store
      storeTitle: 'PHONESTREAM STORE',
      storeSubtitle: 'Browse and purchase premium music products, song catalogs, and more',
      storeFeaturedOnly: 'Featured Only',
      storeLoadingProducts: 'Loading products...',
      storeNoProductsTitle: 'No Products Found',
      storeNoProductsDesc: 'Check back soon for new products!',
      storeViewDetails: 'View Details',
      storeFailedToLoadProducts: 'Failed to load products',
      storeCategoryAll: 'All Products',
      storeCategoryMusicAlbum: 'Music Albums',
      storeCategorySongCatalog: 'Song Catalogs',
      storeCategoryBeat: 'Beats',
      storeCategoryInstrumental: 'Instrumentals',
      storeCategorySamplePack: 'Sample Packs',
      storeCategoryOther: 'Other',

      // SMM Panel (white-labeled)
      smmPanelTitle: 'PHONESTREAM SMM',
      smmPanelSubtitle: 'Place and track your orders from here.',
      smmHome: 'Home',
      smmNewOrder: 'New order',
      smmOrders: 'Orders',
      smmServices: 'Services',
      smmBalance: 'Balance',
      smmCredits: 'Credits',
      smmRefreshingServices: 'Refreshing services…',
      smmRefreshingBalance: 'Refreshing balance…',
      smmRefreshingCredits: 'Refreshing credits…',
      smmAccountBalance: 'Account Balance',
      smmYourCredits: 'Your Credits',
      smmAvailable: 'Available',
      smmReserved: 'Reserved',
      smmAddFunds: 'Add funds (USDT)',
      smmDepositAmount: 'Deposit amount',
      smmDeposit: 'Deposit',
      smmDepositing: 'Depositing…',
      smmDepositHelp: 'Deposits are held in escrow and used to pay for orders. No withdrawals during testing.',
      smmTotalOrders: 'Total Orders',
      smmSelectedPlatform: 'Selected Platform',
      smmStatus: 'Status',
      smmReady: 'Ready',
      smmSelectServiceStatus: 'Select service',
      smmPlaceYourOrder: 'Place your order',
      smmNewOrderTab: 'New Order',
      smmSearchTab: 'Search',
      smmMassOrderTab: 'Mass order',
      smmComingSoon: 'Coming soon.',
      smmCategory: 'Category',
      smmAllCategories: 'All Categories',
      smmService: 'Service',
      smmSelectService: 'Select a service',
      smmLoadingServices: 'Loading services…',
      smmLink: 'Link',
      smmQuantity: 'Quantity',
      smmEstTotal: 'Est. total:',
      smmPlaceOrder: 'Place Order',
      smmPlacingOrder: 'Placing Order…',
      smmReset: 'Reset',
      smmServicePreview: 'Service preview',
      smmSelectServicePreview: 'Select a service to see details here.',
      smmDescription: 'Description',
      smmMin: 'Min',
      smmMax: 'Max',
      smmRefill: 'Refill',
      smmCancel: 'Cancel',
      smmYes: 'Yes',
      smmNo: 'No',
      smmMyOrders: 'My orders',
      smmRefresh: 'Refresh',
      smmRefreshAll: 'Refresh all',
      smmLoadingOrders: 'Loading orders…',
      smmNoOrders: 'No orders yet. Place your first order from “New order”.',
      smmConnectToUse: 'Connect your wallet to place and track orders.',
      smmPanelLockedTitle: 'SMM Panel',
      smmOrder: 'Order',
      smmQty: 'Qty',
      smmRemains: 'Remains',
      smmFailedToLoadServices: 'Failed to load services',
      smmFailedToLoadOrders: 'Failed to load orders',
      smmPleaseEnterLink: 'Please enter a link',
      smmQuantityBetween: 'Quantity must be between {min} and {max}',
      smmOrderPlaced: 'Order placed successfully! Order ID:',
      smmFailedToPlaceOrder: 'Failed to place order',
      smmCancelConfirm: 'Are you sure you want to cancel this order?',
      smmSignInRequiredTitle: 'Sign in required',
      smmSignInRequiredBody: 'To use the SMM panel, please sign the login message with your wallet.',
      smmSignInNow: 'Sign in now',
      smmSignInExpired: 'Session expired. Please sign in again.',
      
      // Hero
      heroTitle: 'PHONESTREAM Revenue Sharing Platform',
      heroSubtitle: 'Mint unique NFTs, track your streaming revenue, and earn passive income from music streaming platforms.',
      connectWallet: 'Connect Wallet',
      goToDashboard: 'Go to Dashboard',
      
      // Sections
      whyChoose: 'Why Choose PhoneStream?',
      features: 'Platform Features',
      guide: 'Platform Guide',
      whitepaper: 'Whitepaper',
      pitchDeck: 'Pitch Deck',
      partners: 'Partners',
      
      // Features Section
      feature1Title: 'User Dashboard',
      feature1Desc: 'Monitor your NFTs, track streaming data, view revenue reports, and manage your portfolio',
      feature2Title: 'Binance Smart Chain',
      feature2Desc: 'Comprehensive tools for visualizing hardware status and revenue performance built on BSC.',
      feature3Title: 'Spotify Integration',
      feature3Desc: 'Seamless integration with Spotify API to fetch your streaming statistics',
      feature4Title: 'BSC Blockchain',
      feature4Desc: 'Built on Binance Smart Chain for fast, low-cost transactions',
      
      // Benefits Section
      benefit1Title: 'Streaming Data',
      benefit1Desc: 'Track your music streaming performance across platforms with AI-powered analytics',
      benefit2Title: 'Automatic Revenue Distribution',
      benefit2Desc: 'Earn royalties automatically based on your NFT ownership and streaming data',
      benefit3Title: 'Generative NFT Collection',
      benefit3Desc: 'Own unique generative NFTs with randomized attributes and premium skins',
      benefit4Title: 'Secure & Transparent',
      benefit4Desc: 'Blockchain-powered platform with full transparency and enterprise-grade security',
      
      // PDF Viewer
      viewGuide: 'View Guide',
      hideGuide: 'Hide Guide',
      viewWhitepaper: 'View Whitepaper',
      hideWhitepaper: 'Hide Whitepaper',
      viewPitchDeck: 'View Pitch Deck',
      hidePitchDeck: 'Hide Pitch Deck',
      downloadPDF: 'Download PDF',
      openFullScreen: 'Open Full Screen',
      readOnly: 'Read-only view - Scroll to navigate',
      mobileScroll: '📱 Tap and scroll to navigate all pages',
      
      // Language
      language: 'Language',
      english: 'English',
      spanish: 'Español',
      
      // Wallet
      noWalletsFound: 'No wallets detected. Please install a wallet:',
      connecting: 'Connecting...',
      clickToConnect: 'Click to connect',
      clickToInstall: 'Click to install',
      opening: 'Opening installation page...',
      walletSecurityNote: 'By connecting, you agree to the terms of service and privacy policy.',
      
      // Add to Wallet
      addToWallet: 'Add to Wallet',
      pleaseConnectWallet: 'Please connect your wallet first',
      noWalletFound: 'No wallet provider found',
      alreadyOnBSC: 'You are already connected to BSC Mainnet',
      networkSwitched: 'Successfully switched to BSC Mainnet',
      networkAdded: 'BSC Mainnet added to your wallet',
      failedToAddNetwork: 'Failed to add network',
      switchToBSCFirst: 'Please switch to BSC Mainnet first',
      tokenAdded: 'PHONE token added to your wallet',
      failedToAddToken: 'Failed to add token',
      adding: 'Adding...',
      onBSC: 'On BSC Mainnet',
      addBSCNetwork: 'Add BSC Network',
      addPHONEToken: 'Add PHONE Token',
      addToWalletNote: 'Connect your wallet and click the buttons above to add BSC Mainnet and PHONE token.',
      viewOnBSCScan: 'View $PHONE Token on BSCScan',
      
      // Minting Section
      presaleMinting: 'Presale Minting',
      mintDescription: 'Mint your unique Boxphone NFT now! 10% of the collection will be available for presale.',
      minted: 'Minted',
      price: 'Price',
      remaining: 'Remaining',
      minting: 'Minting...',
      connectWalletToMint: 'Connect Wallet to Mint',
      soldOut: 'Sold Out',
      mintNFT: 'Mint NFT',
      presaleComplete: 'Presale is complete! Remaining NFTs will be available on OpenSea.',
      pleaseLoginFirst: 'Please login first',
      mintingWillBeImplemented: 'Minting functionality will be implemented with the smart contract',
      mintingFailed: 'Minting failed',
      approveUSDT: 'Approve USDT',
      approvingUSDT: 'Approving USDT...',
      usdtApproved: 'USDT approved successfully!',
      approvalFailed: 'USDT approval failed',
      approveUSDTFirst: 'Please approve USDT first',
      contractNotDeployed: 'NFT contract not deployed yet',
      nftMintedSuccessfully: 'NFT minted successfully!',
      alreadyMinted: 'Already minted',
      transactionRejected: 'Transaction rejected by user',
      
      // About Us Section
      aboutUs: 'About Us',
      aboutUsWelcome: 'Welcome to PhoneStream, your ultimate hub for everything related to the future of music streaming and NFT ownership! We are thrilled to have you join our community of passionate music enthusiasts, artists, and investors who are revolutionizing the music industry.',
      aboutUsMission: 'Our Mission',
      aboutUsMissionText: 'At PhoneStream, we combine the power of AI-generated music, NFT ownership, and a network of custom hardware devices to create a decentralized, revenue-generating music ecosystem. Our mission is to empower artists and provide passive income opportunities for NFT holders through innovative technology and AI-driven content.',
      aboutUsOffer: 'What We Offer',
      aboutUsOffer1: 'AI-Generated Music: Experience high-quality, unique music tracks curated and distributed through legitimate channels.',
      aboutUsOffer2: 'NFT Ownership: Mint generative NFTs that represent ownership of a dedicated streaming machine, operating 24/7 to generate continuous revenue.',
      aboutUsOffer3: 'Transparent Revenue Sharing: Enjoy real-time streaming data and revenue tracking through a dedicated user dashboard.',
      aboutUsOffer4: 'Community Engagement: Connect with like-minded individuals, share feedback, and participate in exclusive events and discussions.',
      aboutUsJoin: 'Join the Revolution',
      aboutUsJoinText: 'Whether you\'re an artist looking to distribute your music, an investor seeking new opportunities, or a music lover eager to be part of the future, PhoneStream is the place for you. Together, we are building a sustainable and lucrative ecosystem for all participants.',
      
      // FAQ Section
      faq: 'Frequently Asked Questions',
      faqIntro: 'Here are some frequently asked questions to help you get started and make the most of your experience with us.',
      faqGeneral: 'General Questions',
      faqNFT: 'NFT and Ownership',
      faqRevenue: 'Revenue and Earnings',
      faqCommunity: 'Community and Engagement',
      faqTechnical: 'Technical Support',
      faqQ1: 'What is PhoneStream?',
      faqA1: 'PhoneStream is a revolutionary platform that combines AI-generated music, NFT ownership, and a network of custom hardware devices to create a decentralized, revenue-generating music ecosystem.',
      faqQ2: 'How does PhoneStream work?',
      faqA2: 'By minting a PhoneStream NFT, you gain ownership of a dedicated streaming machine (Boxphone) that operates 24/7, generating revenue through legitimate streaming platforms. Revenue is distributed to NFT holders based on their ownership tier and the performance of their assigned artist.',
      faqQ3: 'What is the purpose of the PhoneStream Discord Server?',
      faqA3: 'The PhoneStream Discord Server is your ultimate hub for connecting with the community, staying updated with the latest news, sharing feedback, and participating in exclusive events and discussions.',
      faqQ4: 'What is an NFT?',
      faqA4: 'An NFT (Non-Fungible Token) is a unique digital asset that represents ownership of a specific item, in this case, a dedicated streaming machine and an AI-generated artist.',
      faqQ5: 'How do I mint a PhoneStream NFT?',
      faqA5: 'To mint a PhoneStream NFT, connect your wallet to our platform, choose your preferred NFT, and follow the minting process. Each NFT represents ownership of a Boxphone device and an AI-generated artist.',
      faqQ6: 'What are the benefits of owning a PhoneStream NFT?',
      faqA6: 'Benefits include: Revenue Generation: Receive a percentage of revenue generated by your assigned artist, distributed monthly. AI Music Catalog: Access to an exclusive catalog of AI-generated music. Transparency: Real-time streaming data and revenue tracking through a dedicated user dashboard. Community: Exclusive access to the PhoneStream community, including forums, updates, and special events.',
      faqQ7: 'How does the revenue sharing model work?',
      faqA7: 'Revenue is distributed to NFT holders based on their ownership tier and the performance of their assigned artist. The revenue sharing model is tier-based, with higher tiers receiving a larger share of the revenue.',
      faqQ8: 'When and how do I receive my earnings?',
      faqA8: 'Earnings are distributed monthly, directly to your connected wallet. You can track your earnings in real-time through the dedicated user dashboard.',
      faqQ9: 'What factors affect my earnings?',
      faqA9: 'Factors affecting your earnings include: Ownership Tier: Higher tiers receive a larger share of the revenue. Artist Performance: The more streams and engagement your assigned artist receives, the higher your earnings. Market Conditions: Revenue can fluctuate based on market conditions and the performance of the streaming platforms.',
      faqQ10: 'How can I engage with the community?',
      faqA10: 'You can engage with the community by: Joining our various channels to discuss topics, share feedback, and connect with other members. Participating in exclusive events and giveaways. Sharing your creations and getting featured in our community events. Providing feedback and suggestions to help shape the future of PhoneStream.',
      faqQ11: 'How can I stay updated with the latest news and updates?',
      faqA11: 'Stay updated by regularly checking the #announcements channel and following our official social media channels.',
      faqQ12: 'I\'m having trouble connecting my wallet. What should I do?',
      faqA12: 'Ensure you are using a compatible wallet (e.g., MetaMask) and follow the connection instructions provided on our website. If you encounter issues, seek help in the #community-help channel.',
      faqQ13: 'How can I report a bug or issue?',
      faqA13: 'Report bugs or issues in the #community-help channel, providing detailed information about the problem. Our support team will assist you promptly.',
      faqQ14: 'Where can I find more information about PhoneStream?',
      faqA14: 'For more information, visit our official website and explore our updated whitepaper, guides, and pitch deck.',
      faqJoinDiscord: 'Join the PhoneStream Discord Server today and be part of the future of music!',

      // Play Page - Game Hub
      luckyDraw: 'Lucky Draw',
      predictions: 'Predictions',
      casinoGames: 'Casino Games',
      casinoComingSoon: 'Casino games are coming soon. Stay tuned!',
      gameBalance: 'Game Balance',
      totalWon: 'Total Won',
      noActiveDraws: 'No active draws right now. Check back soon!',
      noActivePredictions: 'No active predictions right now. Check back soon!',
      connectWalletFirst: 'Please connect your wallet first',
      entrySuccess: 'Entry purchased successfully!',
      entryFailed: 'Failed to enter draw',
      enterDraw: 'Enter Draw',
      entryFee: 'Entry Fee',
      entries: 'Entries',
      prizePool: 'Prize Pool',
      myEntries: 'My Entries',
      won: 'Won',
      winners: 'Winners',
      processing: 'Processing...',
      enterBetAmount: 'Please enter a bet amount',
      betPlaced: 'Bet placed successfully!',
      betFailed: 'Failed to place bet',
      bet: 'Bet',
      myBets: 'My Bets',
      lost: 'Lost',
      winner: 'Winner',
      pool: 'Pool',

      // Admin - Discounts
      smmDiscounts: 'SMM Discounts',
      addDiscount: 'Add Discount',
      editDiscount: 'Edit Discount',
      discountPercent: 'Discount %',
      reason: 'Reason',
      discountReasonPlaceholder: 'e.g. Lucky Draw Prize, VIP',
      expiresAt: 'Expires At',
      expires: 'Expires',
      permanent: 'Permanent',
      active: 'Active',
      actions: 'Actions',
      noDiscounts: 'No discounts configured',
      confirmDeleteDiscount: 'Are you sure you want to delete this discount?',
      discountCreated: 'Discount created',
      discountUpdated: 'Discount updated',
      discountDeleted: 'Discount deleted',
      failedToLoadDiscounts: 'Failed to load discounts',
      failedToSaveDiscount: 'Failed to save discount',
      failedToUpdateDiscount: 'Failed to update discount',
      failedToDeleteDiscount: 'Failed to delete discount',
      yes: 'Yes',
      no: 'No',
      save: 'Save',
      cancel: 'Cancel',
      edit: 'Edit',
      delete: 'Delete',
      loading: 'Loading...',
      walletAddress: 'Wallet Address',

      // Admin - Games
      luckyDraws: 'Lucky Draws',
      createDraw: 'Create Draw',
      invalidEntryFee: 'Please enter a valid entry fee',
      drawCreated: 'Draw created successfully',
      failedToCreateDraw: 'Failed to create draw',
      failedToLoadDraws: 'Failed to load draws',
      confirmExecuteDraw: 'Execute this draw and select winners? This cannot be undone.',
      drawExecuted: 'Draw executed successfully',
      failedToExecuteDraw: 'Failed to execute draw',
      confirmCancelDraw: 'Cancel this draw and refund all entries?',
      drawCancelled: 'Draw cancelled',
      failedToCancelDraw: 'Failed to cancel draw',
      executeDraw: 'Execute Draw',
      cancelDraw: 'Cancel Draw',
      noDraws: 'No lucky draws created yet',
      title: 'Title',
      description: 'Description',
      entryFeeUsd: 'Entry Fee (USD)',
      maxEntries: 'Max Entries',
      maxEntriesPerUser: 'Max Per User',
      unlimited: 'unlimited',
      startsAt: 'Starts At',
      endsAt: 'Ends At',
      start: 'Start',
      end: 'End',
      prizes: 'Prizes',
      prizeLabel: 'Prize Label',
      quantity: 'Qty',
      addPrize: 'Add Prize',

      createPrediction: 'Create Prediction',
      predictionCreated: 'Prediction created successfully',
      failedToCreatePrediction: 'Failed to create prediction',
      failedToLoadPredictions: 'Failed to load predictions',
      confirmResolvePrediction: 'Resolve this prediction with the selected winner? This cannot be undone.',
      predictionResolved: 'Prediction resolved',
      failedToResolvePrediction: 'Failed to resolve prediction',
      confirmCancelPrediction: 'Cancel this prediction and refund all bets?',
      predictionCancelled: 'Prediction cancelled',
      failedToCancelPrediction: 'Failed to cancel prediction',
      cancelPrediction: 'Cancel Prediction',
      resolveAsWinner: 'Resolve as Winner',
      noPredictions: 'No predictions created yet',
      question: 'Question',
      options: 'Options',
      option: 'Option',
      addOption: 'Add Option',
      atLeastTwoOptions: 'At least two options required',
      minBetUsd: 'Min Bet (USD)',
      maxBetUsd: 'Max Bet (USD)',
      platformFee: 'Platform Fee',
      optional: 'optional',
    },
    es: {
      smmSignInExpired: 'Sesión caducada. Por favor, inicia sesión nuevamente.',
      // Navigation
      home: 'Inicio',
      dashboard: 'Panel',
      store: 'Tienda',
      play: 'Jugar',
      marketplace: 'Mercado',
      admin: 'Admin',
      termsAndConditions: 'Términos y Condiciones',
      privacyPolicy: 'Política de Privacidad',
      masterServiceAgreement: 'Acuerdo Marco de Servicios',
      wallet: 'Billetera',
      notConnected: 'No conectado',
      callbackDomain: 'Dominio de Callback',
      gameLaunchUrl: 'URL de Lanzamiento',
      configured: 'Configurado',
      missingPlayUrl: 'Falta configurar - define REACT_APP_PLAY_URL o pega una URL abajo',
      lobbyGameUrl: 'URL de lobby / juego',
      playUrlPlaceholder: 'https://ejemplo-url-del-lobby',
      openGame: 'Abrir Juego',
      iframeBlockedHint: 'Algunos proveedores bloquean iframes. Si el cuadro aparece en blanco, usa Abrir Juego.',
      enterLobbyUrlHint: 'Ingresa una URL de lobby arriba para empezar a probar el flujo de juego.',
      playSubtitle: 'Lanza tu lobby de casino y juegos desde aquí. Los callbacks de billetera seamless ya están integrados en tu backend.',

      // Store
      storeTitle: 'PHONESTREAM STORE',
      storeSubtitle: 'Explora y compra productos premium de música, catálogos de canciones y más',
      storeFeaturedOnly: 'Solo Destacados',
      storeLoadingProducts: 'Cargando productos...',
      storeNoProductsTitle: 'No se encontraron productos',
      storeNoProductsDesc: '¡Vuelve pronto para nuevos productos!',
      storeViewDetails: 'Ver detalles',
      storeFailedToLoadProducts: 'No se pudieron cargar los productos',
      storeCategoryAll: 'Todos los productos',
      storeCategoryMusicAlbum: 'Álbumes',
      storeCategorySongCatalog: 'Catálogos de canciones',
      storeCategoryBeat: 'Beats',
      storeCategoryInstrumental: 'Instrumentales',
      storeCategorySamplePack: 'Packs de samples',
      storeCategoryOther: 'Otros',

      // SMM Panel (white-labeled)
      smmPanelTitle: 'PHONESTREAM SMM',
      smmPanelSubtitle: 'Coloca y rastrea tus pedidos desde aquí.',
      smmHome: 'Inicio',
      smmNewOrder: 'Nuevo pedido',
      smmOrders: 'Pedidos',
      smmServices: 'Servicios',
      smmBalance: 'Saldo',
      smmCredits: 'Créditos',
      smmRefreshingServices: 'Actualizando servicios…',
      smmRefreshingBalance: 'Actualizando saldo…',
      smmRefreshingCredits: 'Actualizando créditos…',
      smmAccountBalance: 'Saldo de la cuenta',
      smmYourCredits: 'Tus créditos',
      smmAvailable: 'Disponible',
      smmReserved: 'Reservado',
      smmAddFunds: 'Agregar fondos (USDT)',
      smmDepositAmount: 'Monto a depositar',
      smmDeposit: 'Depositar',
      smmDepositing: 'Depositando…',
      smmDepositHelp: 'Los depósitos quedan en escrow y se usan para pagar pedidos. Sin retiros durante las pruebas.',
      smmTotalOrders: 'Total de pedidos',
      smmSelectedPlatform: 'Plataforma seleccionada',
      smmStatus: 'Estado',
      smmReady: 'Listo',
      smmSelectServiceStatus: 'Selecciona un servicio',
      smmPlaceYourOrder: 'Realiza tu pedido',
      smmNewOrderTab: 'Nuevo',
      smmSearchTab: 'Buscar',
      smmMassOrderTab: 'Pedido masivo',
      smmComingSoon: 'Próximamente.',
      smmCategory: 'Categoría',
      smmAllCategories: 'Todas las categorías',
      smmService: 'Servicio',
      smmSelectService: 'Selecciona un servicio',
      smmLoadingServices: 'Cargando servicios…',
      smmLink: 'Enlace',
      smmQuantity: 'Cantidad',
      smmEstTotal: 'Total estimado:',
      smmPlaceOrder: 'Realizar pedido',
      smmPlacingOrder: 'Realizando pedido…',
      smmReset: 'Restablecer',
      smmServicePreview: 'Vista previa del servicio',
      smmSelectServicePreview: 'Selecciona un servicio para ver los detalles aquí.',
      smmDescription: 'Descripción',
      smmMin: 'Mín',
      smmMax: 'Máx',
      smmRefill: 'Recarga',
      smmCancel: 'Cancelar',
      smmYes: 'Sí',
      smmNo: 'No',
      smmMyOrders: 'Mis pedidos',
      smmRefresh: 'Actualizar',
      smmRefreshAll: 'Actualizar todo',
      smmLoadingOrders: 'Cargando pedidos…',
      smmNoOrders: 'Aún no hay pedidos. Haz tu primer pedido desde “Nuevo pedido”.',
      smmConnectToUse: 'Conecta tu billetera para realizar y rastrear pedidos.',
      smmPanelLockedTitle: 'Panel SMM',
      smmOrder: 'Pedido',
      smmQty: 'Cant.',
      smmRemains: 'Restantes',
      smmFailedToLoadServices: 'No se pudieron cargar los servicios',
      smmFailedToLoadOrders: 'No se pudieron cargar los pedidos',
      smmPleaseEnterLink: 'Por favor ingresa un enlace',
      smmQuantityBetween: 'La cantidad debe estar entre {min} y {max}',
      smmOrderPlaced: '¡Pedido realizado con éxito! ID del pedido:',
      smmFailedToPlaceOrder: 'No se pudo realizar el pedido',
      smmCancelConfirm: '¿Estás seguro de que quieres cancelar este pedido?',
      smmSignInRequiredTitle: 'Inicio de sesión requerido',
      smmSignInRequiredBody: 'Para usar el panel SMM, firma el mensaje de inicio de sesión con tu billetera.',
      smmSignInNow: 'Iniciar sesión',
      
      // Hero
      heroTitle: 'Plataforma de Participación de Ingresos PHONESTREAM',
      heroSubtitle: 'Mintea NFTs únicos, rastrea tus ingresos por streaming y gana ingresos pasivos de plataformas de streaming de música.',
      connectWallet: 'Conectar Billetera',
      goToDashboard: 'Ir al Panel',
      
      // Sections
      whyChoose: '¿Por qué elegir PhoneStream?',
      features: 'Características de la Plataforma',
      guide: 'Guía de la Plataforma',
      whitepaper: 'Documento Técnico',
      pitchDeck: 'Presentación',
      partners: 'Socios',
      
      // Features Section
      feature1Title: 'Panel de Usuario',
      feature1Desc: 'Monitorea tus NFTs, rastrea datos de streaming, visualiza reportes de ingresos y administra tu portafolio',
      feature2Title: 'Binance Smart Chain',
      feature2Desc: 'Herramientas integrales para visualizar el estado del hardware y el rendimiento de ingresos construidas en BSC.',
      feature3Title: 'Integración con Spotify',
      feature3Desc: 'Integración perfecta con la API de Spotify para obtener tus estadísticas de streaming',
      feature4Title: 'Blockchain BSC',
      feature4Desc: 'Construido en Binance Smart Chain para transacciones rápidas y de bajo costo',
      
      // Benefits Section
      benefit1Title: 'Datos de Streaming',
      benefit1Desc: 'Rastrea el rendimiento de tu música en streaming en todas las plataformas con análisis impulsados por IA',
      benefit2Title: 'Distribución Automática de Ingresos',
      benefit2Desc: 'Gana regalías automáticamente basadas en tu propiedad de NFT y datos de streaming',
      benefit3Title: 'Colección NFT Generativa',
      benefit3Desc: 'Posee NFTs generativos únicos con atributos aleatorios y skins premium',
      benefit4Title: 'Seguro y Transparente',
      benefit4Desc: 'Plataforma impulsada por blockchain con total transparencia y seguridad de nivel empresarial',
      
      // PDF Viewer
      viewGuide: 'Ver Guía',
      hideGuide: 'Ocultar Guía',
      viewWhitepaper: 'Ver Documento Técnico',
      hideWhitepaper: 'Ocultar Documento Técnico',
      viewPitchDeck: 'Ver Presentación',
      hidePitchDeck: 'Ocultar Presentación',
      downloadPDF: 'Descargar PDF',
      openFullScreen: 'Abrir Pantalla Completa',
      readOnly: 'Vista de solo lectura - Desplázate para navegar',
      mobileScroll: '📱 Toca y desplázate para navegar todas las páginas',
      
      // Language
      language: 'Idioma',
      english: 'English',
      spanish: 'Español',
      
      // Wallet
      noWalletsFound: 'No se detectaron billeteras. Por favor instala una billetera:',
      connecting: 'Conectando...',
      clickToConnect: 'Haz clic para conectar',
      clickToInstall: 'Haz clic para instalar',
      opening: 'Abriendo página de instalación...',
      walletSecurityNote: 'Al conectar, aceptas los términos de servicio y política de privacidad.',
      
      // Add to Wallet
      addToWallet: 'Agregar a Billetera',
      pleaseConnectWallet: 'Por favor conecta tu billetera primero',
      noWalletFound: 'No se encontró proveedor de billetera',
      alreadyOnBSC: 'Ya estás conectado a BSC Mainnet',
      networkSwitched: 'Cambiado exitosamente a BSC Mainnet',
      networkAdded: 'BSC Mainnet agregado a tu billetera',
      failedToAddNetwork: 'Error al agregar red',
      switchToBSCFirst: 'Por favor cambia a BSC Mainnet primero',
      tokenAdded: 'Token PHONE agregado a tu billetera',
      failedToAddToken: 'Error al agregar token',
      adding: 'Agregando...',
      onBSC: 'En BSC Mainnet',
      addBSCNetwork: 'Agregar Red BSC',
      addPHONEToken: 'Agregar Token PHONE',
      addToWalletNote: 'Conecta tu billetera y haz clic en los botones de arriba para agregar BSC Mainnet y el token PHONE.',
      viewOnBSCScan: 'Ver Token $PHONE en BSCScan',
      
      // Minting Section
      presaleMinting: 'Preventa de NFT',
      mintDescription: '¡Mintea tu Boxphone NFT único ahora! El 10% de la colección estará disponible para preventa.',
      minted: 'Minteados',
      price: 'Precio',
      remaining: 'Restantes',
      minting: 'Minteando...',
      connectWalletToMint: 'Conectar Billetera para Mintear',
      soldOut: 'Agotado',
      mintNFT: 'Mintear NFT',
      presaleComplete: '¡La preventa está completa! Los NFTs restantes estarán disponibles en OpenSea.',
      pleaseLoginFirst: 'Por favor inicia sesión primero',
      mintingWillBeImplemented: 'La funcionalidad de minteo se implementará con el contrato inteligente',
      mintingFailed: 'Error al mintear',
      approveUSDT: 'Aprobar USDT',
      approvingUSDT: 'Aprobando USDT...',
      usdtApproved: '¡USDT aprobado exitosamente!',
      approvalFailed: 'Error al aprobar USDT',
      approveUSDTFirst: 'Por favor aprueba USDT primero',
      contractNotDeployed: 'Contrato NFT aún no desplegado',
      nftMintedSuccessfully: '¡NFT minteado exitosamente!',
      alreadyMinted: 'Ya minteado',
      transactionRejected: 'Transacción rechazada por el usuario',
      
      // About Us Section
      aboutUs: 'Sobre Nosotros',
      aboutUsWelcome: '¡Bienvenido a PhoneStream, tu centro definitivo para todo lo relacionado con el futuro del streaming de música y la propiedad de NFT! Estamos encantados de que te unas a nuestra comunidad de apasionados entusiastas de la música, artistas e inversionistas que están revolucionando la industria musical.',
      aboutUsMission: 'Nuestra Misión',
      aboutUsMissionText: 'En PhoneStream, combinamos el poder de la música generada por IA, la propiedad de NFT y una red de dispositivos de hardware personalizados para crear un ecosistema musical descentralizado y generador de ingresos. Nuestra misión es empoderar a los artistas y brindar oportunidades de ingresos pasivos para los titulares de NFT a través de tecnología innovadora y contenido impulsado por IA.',
      aboutUsOffer: 'Lo que Ofrecemos',
      aboutUsOffer1: 'Música Generada por IA: Experimenta pistas musicales únicas y de alta calidad curadas y distribuidas a través de canales legítimos.',
      aboutUsOffer2: 'Propiedad de NFT: Mintea NFTs generativos que representan la propiedad de una máquina de streaming dedicada, operando las 24 horas del día, los 7 días de la semana para generar ingresos continuos.',
      aboutUsOffer3: 'Distribución Transparente de Ingresos: Disfruta de datos de streaming en tiempo real y seguimiento de ingresos a través de un panel de usuario dedicado.',
      aboutUsOffer4: 'Compromiso Comunitario: Conéctate con personas de ideas afines, comparte comentarios y participa en eventos y discusiones exclusivos.',
      aboutUsJoin: 'Únete a la Revolución',
      aboutUsJoinText: 'Ya seas un artista que busca distribuir tu música, un inversionista que busca nuevas oportunidades, o un amante de la música ansioso por ser parte del futuro, PhoneStream es el lugar para ti. Juntos, estamos construyendo un ecosistema sostenible y lucrativo para todos los participantes.',
      
      // FAQ Section
      faq: 'Preguntas Frecuentes',
      faqIntro: 'Aquí hay algunas preguntas frecuentes para ayudarte a comenzar y aprovechar al máximo tu experiencia con nosotros.',
      faqGeneral: 'Preguntas Generales',
      faqNFT: 'NFT y Propiedad',
      faqRevenue: 'Ingresos y Ganancias',
      faqCommunity: 'Comunidad y Participación',
      faqTechnical: 'Soporte Técnico',
      faqQ1: '¿Qué es PhoneStream?',
      faqA1: 'PhoneStream es una plataforma revolucionaria que combina música generada por IA, propiedad de NFT y una red de dispositivos de hardware personalizados para crear un ecosistema musical descentralizado y generador de ingresos.',
      faqQ2: '¿Cómo funciona PhoneStream?',
      faqA2: 'Al mintear un NFT de PhoneStream, obtienes la propiedad de una máquina de streaming dedicada (Boxphone) que opera las 24 horas del día, los 7 días de la semana, generando ingresos a través de plataformas de streaming legítimas. Los ingresos se distribuyen a los titulares de NFT según su nivel de propiedad y el rendimiento de su artista asignado.',
      faqQ3: '¿Cuál es el propósito del servidor de Discord de PhoneStream?',
      faqA3: 'El servidor de Discord de PhoneStream es tu centro definitivo para conectarte con la comunidad, mantenerte actualizado con las últimas noticias, compartir comentarios y participar en eventos y discusiones exclusivos.',
      faqQ4: '¿Qué es un NFT?',
      faqA4: 'Un NFT (Token No Fungible) es un activo digital único que representa la propiedad de un elemento específico, en este caso, una máquina de streaming dedicada y un artista generado por IA.',
      faqQ5: '¿Cómo minteo un NFT de PhoneStream?',
      faqA5: 'Para mintear un NFT de PhoneStream, conecta tu billetera a nuestra plataforma, elige tu NFT preferido y sigue el proceso de minteo. Cada NFT representa la propiedad de un dispositivo Boxphone y un artista generado por IA.',
      faqQ6: '¿Cuáles son los beneficios de poseer un NFT de PhoneStream?',
      faqA6: 'Los beneficios incluyen: Generación de Ingresos: Recibe un porcentaje de los ingresos generados por tu artista asignado, distribuido mensualmente. Catálogo de Música IA: Acceso a un catálogo exclusivo de música generada por IA. Transparencia: Datos de streaming en tiempo real y seguimiento de ingresos a través de un panel de usuario dedicado. Comunidad: Acceso exclusivo a la comunidad de PhoneStream, incluidos foros, actualizaciones y eventos especiales.',
      faqQ7: '¿Cómo funciona el modelo de distribución de ingresos?',
      faqA7: 'Los ingresos se distribuyen a los titulares de NFT según su nivel de propiedad y el rendimiento de su artista asignado. El modelo de distribución de ingresos se basa en niveles, con niveles más altos recibiendo una mayor parte de los ingresos.',
      faqQ8: '¿Cuándo y cómo recibo mis ganancias?',
      faqA8: 'Las ganancias se distribuyen mensualmente, directamente a tu billetera conectada. Puedes rastrear tus ganancias en tiempo real a través del panel de usuario dedicado.',
      faqQ9: '¿Qué factores afectan mis ganancias?',
      faqA9: 'Los factores que afectan tus ganancias incluyen: Nivel de Propiedad: Los niveles más altos reciben una mayor parte de los ingresos. Rendimiento del Artista: Cuantos más streams y participación reciba tu artista asignado, mayores serán tus ganancias. Condiciones del Mercado: Los ingresos pueden fluctuar según las condiciones del mercado y el rendimiento de las plataformas de streaming.',
      faqQ10: '¿Cómo puedo participar en la comunidad?',
      faqA10: 'Puedes participar en la comunidad: Uniéndote a nuestros diversos canales para discutir temas, compartir comentarios y conectarte con otros miembros. Participando en eventos y sorteos exclusivos. Compartiendo tus creaciones y siendo destacado en nuestros eventos comunitarios. Proporcionando comentarios y sugerencias para ayudar a dar forma al futuro de PhoneStream.',
      faqQ11: '¿Cómo puedo mantenerme actualizado con las últimas noticias y actualizaciones?',
      faqA11: 'Mantente actualizado revisando regularmente el canal de Discord #announcements y siguiendo nuestros canales oficiales de redes sociales.',
      faqQ12: 'Tengo problemas para conectar mi billetera. ¿Qué debo hacer?',
      faqA12: 'Asegúrate de estar usando una billetera compatible (por ejemplo, MetaMask) y sigue las instrucciones de conexión proporcionadas en nuestro sitio web. Si encuentras problemas, busca ayuda en el canal #community-help.',
      faqQ13: '¿Cómo puedo reportar un error o problema?',
      faqA13: 'Reporta errores o problemas en nuestro canal de Discord #community-help, proporcionando información detallada sobre el problema. Nuestro equipo de soporte te ayudará de inmediato.',
      faqQ14: '¿Dónde puedo encontrar más información sobre PhoneStream?',
      faqA14: 'Para más información, visita nuestro sitio web oficial y explora nuestro whitepaper actualizado, guías y pitch deck.',
      faqJoinDiscord: '¡Únete al servidor de Discord de PhoneStream hoy y sé parte del futuro de la música!',

      // Play Page - Game Hub
      luckyDraw: 'Sorteo',
      predictions: 'Predicciones',
      casinoGames: 'Juegos de Casino',
      casinoComingSoon: '¡Los juegos de casino llegarán pronto. Mantente atento!',
      gameBalance: 'Balance de Juego',
      totalWon: 'Total Ganado',
      noActiveDraws: 'No hay sorteos activos ahora. ¡Vuelve pronto!',
      noActivePredictions: 'No hay predicciones activas ahora. ¡Vuelve pronto!',
      connectWalletFirst: 'Por favor conecta tu billetera primero',
      entrySuccess: '¡Entrada comprada exitosamente!',
      entryFailed: 'Error al entrar al sorteo',
      enterDraw: 'Participar',
      entryFee: 'Costo de Entrada',
      entries: 'Entradas',
      prizePool: 'Pozo de Premios',
      myEntries: 'Mis Entradas',
      won: 'Ganó',
      winners: 'Ganadores',
      processing: 'Procesando...',
      enterBetAmount: 'Por favor ingresa un monto de apuesta',
      betPlaced: '¡Apuesta realizada exitosamente!',
      betFailed: 'Error al realizar apuesta',
      bet: 'Apostar',
      myBets: 'Mis Apuestas',
      lost: 'Perdió',
      winner: 'Ganador',
      pool: 'Pozo',

      // Admin - Discounts
      smmDiscounts: 'Descuentos SMM',
      addDiscount: 'Agregar Descuento',
      editDiscount: 'Editar Descuento',
      discountPercent: 'Descuento %',
      reason: 'Razón',
      discountReasonPlaceholder: 'ej. Premio de Sorteo, VIP',
      expiresAt: 'Expira El',
      expires: 'Expira',
      permanent: 'Permanente',
      active: 'Activo',
      actions: 'Acciones',
      noDiscounts: 'No hay descuentos configurados',
      confirmDeleteDiscount: '¿Estás seguro de que deseas eliminar este descuento?',
      discountCreated: 'Descuento creado',
      discountUpdated: 'Descuento actualizado',
      discountDeleted: 'Descuento eliminado',
      failedToLoadDiscounts: 'Error al cargar descuentos',
      failedToSaveDiscount: 'Error al guardar descuento',
      failedToUpdateDiscount: 'Error al actualizar descuento',
      failedToDeleteDiscount: 'Error al eliminar descuento',
      yes: 'Sí',
      no: 'No',
      save: 'Guardar',
      cancel: 'Cancelar',
      edit: 'Editar',
      delete: 'Eliminar',
      loading: 'Cargando...',
      walletAddress: 'Dirección de Billetera',

      // Admin - Games
      luckyDraws: 'Sorteos',
      createDraw: 'Crear Sorteo',
      invalidEntryFee: 'Por favor ingresa un costo de entrada válido',
      drawCreated: 'Sorteo creado exitosamente',
      failedToCreateDraw: 'Error al crear sorteo',
      failedToLoadDraws: 'Error al cargar sorteos',
      confirmExecuteDraw: '¿Ejecutar este sorteo y seleccionar ganadores? Esto no se puede deshacer.',
      drawExecuted: 'Sorteo ejecutado exitosamente',
      failedToExecuteDraw: 'Error al ejecutar sorteo',
      confirmCancelDraw: '¿Cancelar este sorteo y reembolsar todas las entradas?',
      drawCancelled: 'Sorteo cancelado',
      failedToCancelDraw: 'Error al cancelar sorteo',
      executeDraw: 'Ejecutar Sorteo',
      cancelDraw: 'Cancelar Sorteo',
      noDraws: 'No hay sorteos creados aún',
      title: 'Título',
      description: 'Descripción',
      entryFeeUsd: 'Costo de Entrada (USD)',
      maxEntries: 'Máximo de Entradas',
      maxEntriesPerUser: 'Máximo por Usuario',
      unlimited: 'ilimitado',
      startsAt: 'Inicia El',
      endsAt: 'Termina El',
      start: 'Inicio',
      end: 'Fin',
      prizes: 'Premios',
      prizeLabel: 'Nombre del Premio',
      quantity: 'Cant.',
      addPrize: 'Agregar Premio',

      createPrediction: 'Crear Predicción',
      predictionCreated: 'Predicción creada exitosamente',
      failedToCreatePrediction: 'Error al crear predicción',
      failedToLoadPredictions: 'Error al cargar predicciones',
      confirmResolvePrediction: '¿Resolver esta predicción con el ganador seleccionado? Esto no se puede deshacer.',
      predictionResolved: 'Predicción resuelta',
      failedToResolvePrediction: 'Error al resolver predicción',
      confirmCancelPrediction: '¿Cancelar esta predicción y reembolsar todas las apuestas?',
      predictionCancelled: 'Predicción cancelada',
      failedToCancelPrediction: 'Error al cancelar predicción',
      cancelPrediction: 'Cancelar Predicción',
      resolveAsWinner: 'Resolver como Ganador',
      noPredictions: 'No hay predicciones creadas aún',
      question: 'Pregunta',
      options: 'Opciones',
      option: 'Opción',
      addOption: 'Agregar Opción',
      atLeastTwoOptions: 'Se requieren al menos dos opciones',
      minBetUsd: 'Apuesta Mín. (USD)',
      maxBetUsd: 'Apuesta Máx. (USD)',
      platformFee: 'Comisión',
      optional: 'opcional',
    }
  };

  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    isEnglish: language === 'en',
    isSpanish: language === 'es',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

